import {
    buildEntryGroup,
    buildPhotoGridPages,
    entryBoundsAt,
    hasGroupMetadata,
    isGroupedPreview,
    tileHeightVh,
} from './quickBuyPhotoGrid';

export default () => ({
    opened: false,
    column: null,
    group: [],
    groupPages: [],
    groupPageIndex: 0,
    groupSelected: {},
    packagesLoaded: false,
    zoomedPhoto: null,
    watermark: true,
    canDownload: false,
    canFavorite: false,
    canSave: true,
    mainEventId: null,
    canSelect: false,
    quickBuy: false,
    urlParams: {},
    activeImage: null,
    photos: null,
    quantity: 0,
    event: null,
    swiper: null,
    windowHeight: null,
    checkImages: true,
    downloading: false,
    hasClientRole: false,

    imagesSelected: {},
    packages: [],
    perPage: 0,
    lastLoadedOffset: 0,
    requestedOffsets: [],
    isLoadingImages: false,
    route: null,
    totalImagesCount: 0,
    previousActiveIndex: null,
    lastForwardOffset: 0,
    lastBackwardOffset: 0,
    firstLoad: true,
    currentEventId: null,
    openedAtIndex: null,
    scrollTarget: null,
    activeIndex: 0,
    activeEntryStart: null,
    seedGroup: [],
    skippingGroupMembers: false,

    init(){
        window.addEventListener('resize', () => {
            this.calcContainerHeight();
        });
    },

    set([photos, position, {event='slider-preview::toggle', ...options}]) {
        this.opened = true;
        this.event  = event;
        this.photos = Object.assign({}, photos);
        this.windowHeight = null;

        this.column           = _.get(this.options, 'imageAttr', 'preview');
        this.canDownload      = _.get(options, 'canDownload', true);
        this.canFavorite      = _.get(options, 'canFavorite', false);
        this.canSave          = _.get(options, 'canSave', true);
        this.mainEventId      = _.get(options, 'mainEventId', null);
        this.checkImages      = _.get(options, 'checkImages', true);
        this.imagesSelected   = _.get(options, 'selected', {});
        this.canSelect        = _.get(options, 'canSelect', false);
        this.quickBuy         = _.get(options, 'quickBuy', false);
        this.urlParams        = _.get(options, 'urlParams', {});
        this.quantity         = _.get(options, 'quantity', 0);
        this.perPage          = this.perPage ? this.perPage : _.get(options, 'perPage', 50);
        this.hasClientRole    = _.get(options, 'hasClientRole', false);
        this.route            = _.get(options, 'route', 'customer');
        this.totalImagesCount = _.get(options, 'totalImagesCount', 0);
        this.currentEventId   = _.get(options, 'eventId', null);

        this.watermark  = _.get(options, 'watermark') ?? this.$store.cart.showWatermark();
        this.scrollTarget  = _.get(options, 'scrollTarget', null);

        this.seedGroup        = _.get(options, 'group', []);
        this.activeEntryStart = null;

        const startIndex = this.entryBounds(position).start;

        this.previousActiveIndex = startIndex;
        this.openedAtIndex       = startIndex;
        this.activeIndex         = startIndex;

        this.applyEntryGroup(startIndex);

        if (this.quickBuy) {
            this.canFavorite = false;

            const eventId = this.photos[startIndex]?.event_id
                ?? this.photos[0]?.event_id
                ?? this.currentEventId;

            fetch(`/api-quick-buy-packages/${eventId}`)
                .then(response => response.json())
                .then(data => {
                    this.packages = data;
                    this.packagesLoaded = true;

                    this.enforceSelectionCap();
                });
        }

        this.$nextTick(() => {
            this.calcContainerHeight();
            this.appendSlides(startIndex);
        });
    },

    setGroup(group) {
        this.zoomedPhoto    = null;
        this.packagesLoaded = this.packagesLoaded && this.packages.length > 0;
        this.group          = Array.isArray(group) ? group : [];
        this.groupPages     = isGroupedPreview(this.group) ? buildPhotoGridPages(this.group) : [];
        this.groupPageIndex = 0;
        this.groupSelected  = {};

        if (this.isGrouped()) {
            this.activeImage = this.group[0];

            this.selectAllGroupPhotos();
        }
    },

    selectAllGroupPhotos() {
        this.groupSelected = this.group
            .slice(0, this.maxGroupSelectable())
            .reduce((selected, photo) => {
                selected[photo.id] = photo;

                return selected;
            }, {});
    },

    isGrouped() {
        return this.groupPages.length > 0;
    },

    entryBounds(index) {
        if (!this.quickBuy) {
            return { start: index, end: index };
        }

        return entryBoundsAt(this.photos, index);
    },

    applyEntryGroup(startIndex) {
        this.activeEntryStart = startIndex;

        if (!this.quickBuy) {
            this.setGroup([]);

            return;
        }

        const photo = this.photos?.[startIndex];

        this.setGroup(
            hasGroupMetadata(photo)
                ? buildEntryGroup(this.photos, startIndex)
                : (startIndex === this.openedAtIndex ? this.seedGroup : [])
        );
    },

    syncEntry() {
        const { start } = this.entryBounds(this.activeIndex);

        if (start === this.activeEntryStart) {
            return;
        }

        this.applyEntryGroup(start);
    },

    lastIndex() {
        return Object.keys(this.photos ?? {}).length - 1;
    },

    nextEntryIndex() {
        const { end } = this.entryBounds(this.activeIndex);

        return end + 1 <= this.lastIndex() ? end + 1 : null;
    },

    previousEntryIndex() {
        const { start } = this.entryBounds(this.activeIndex);

        return start > 0 ? this.entryBounds(start - 1).start : null;
    },

    hasNextEntry() {
        return this.nextEntryIndex() !== null;
    },

    hasPreviousEntry() {
        return this.previousEntryIndex() !== null;
    },

    goToEntry(index) {
        if (index === null) {
            return;
        }

        this.swiper?.slideTo(index);
    },

    nextEntry() {
        this.goToEntry(this.nextEntryIndex());
    },

    previousEntry() {
        this.goToEntry(this.previousEntryIndex());
    },

    skipGroupMembers(currentIndex) {
        if (!this.quickBuy || this.skippingGroupMembers) {
            return false;
        }

        const { start, end } = this.entryBounds(currentIndex);

        if (start === end || currentIndex === start) {
            return false;
        }

        const previous       = this.previousActiveIndex ?? currentIndex;
        const cameFromInside = previous >= start && previous <= end;
        const goingForward   = previous < currentIndex;

        const target = goingForward && cameFromInside && end + 1 <= this.lastIndex()
            ? end + 1
            : start;

        if (target === currentIndex) {
            return false;
        }

        this.previousActiveIndex   = currentIndex;
        this.skippingGroupMembers  = true;

        requestAnimationFrame(() => {
            this.skippingGroupMembers = false;
            this.swiper?.slideTo(target, 0);
        });

        return true;
    },

    currentGroupPage() {
        return this.groupPages[this.groupPageIndex] ?? [];
    },

    openGroupZoom(photo) {
        if (!photo) {
            return;
        }

        this.zoomedPhoto = photo;
    },

    closeGroupZoom() {
        this.zoomedPhoto = null;
    },

    zoomedPhotoSource() {
        if (!this.zoomedPhoto) {
            return null;
        }

        return this.watermark
            ? this.zoomedPhoto.watermark_preview
            : this.zoomedPhoto.preview;
    },

    // A cap, not a fixed size: the image keeps its own proportions (full
    // tile width, height following its real aspect ratio) and only shrinks
    // below that if it would otherwise be taller than this budget allows.
    // The team photo's banner tile gets a smaller cap than the rest of its page.
    groupTileStyle(tile) {
        return `max-height: ${tileHeightVh(tile, this.currentGroupPage())}vh`;
    },

    goToGroupPage(index) {
        if (index < 0 || index >= this.groupPages.length) {
            return;
        }

        this.groupPageIndex = index;
    },

    nextGroupPage() {
        this.goToGroupPage(this.groupPageIndex + 1);
    },

    previousGroupPage() {
        this.goToGroupPage(this.groupPageIndex - 1);
    },

    toggleGroupPhoto(photo) {
        if (!photo) {
            return;
        }

        if (this.isGroupPhotoSelected(photo)) {
            delete this.groupSelected[photo.id];

            return;
        }

        if (this.groupSelectedCount() >= this.maxGroupSelectable()) {
            return;
        }

        this.groupSelected[photo.id] = photo;
        this.activeImage = photo;
    },

    isGroupPhotoSelected(photo) {
        return Boolean(photo && this.groupSelected[photo.id]);
    },

    canSelectGroupPhoto(photo) {
        return this.isGroupPhotoSelected(photo)
            || this.groupSelectedCount() < this.maxGroupSelectable();
    },

    groupSelectedCount() {
        return Object.keys(this.groupSelected).length;
    },

    groupSelectedIds() {
        return this.group
            .filter((photo) => this.isGroupPhotoSelected(photo))
            .map((photo) => photo.id);
    },

    packageRange(item) {
        const minimum = Number(item?.min_photos ?? 0);
        const maximum = Number(item?.max_photos ?? 0);

        if (!maximum && item?.is_digital === 'All Photos From Event') {
            return { min: 1, max: this.group.length };
        }

        return { min: minimum, max: maximum };
    },

    maxGroupSelectable() {
        if (!this.packagesLoaded || this.packages.length === 0) {
            return this.group.length;
        }

        return Math.min(
            this.group.length,
            Math.max(...this.packages.map((item) => this.packageRange(item).max || 0), 0) || this.group.length
        );
    },

    enforceSelectionCap() {
        const max = this.maxGroupSelectable();

        if (this.groupSelectedCount() <= max) {
            return;
        }

        const kept = this.groupSelectedIds().slice(0, max);

        this.groupSelected = kept.reduce((selected, id) => {
            selected[id] = this.group.find((photo) => photo.id === id);

            return selected;
        }, {});
    },

    packageFitsSelection(item) {
        const { min, max } = this.packageRange(item);
        const count = this.groupSelectedCount();

        if (count === 0) {
            return false;
        }

        if (!max) {
            return true;
        }

        return count >= min && count <= max;
    },

    availablePackages() {
        if (!this.isGrouped()) {
            return this.packages;
        }

        return this.packages.filter((item) => this.packageFitsSelection(item));
    },

    hasSuggestedPackages() {
        return this.availablePackages().length > 0;
    },

    isPackageEnabled(item) {
        return !this.isGrouped() || this.packageFitsSelection(item);
    },

    // Purchasable packages always come first; the (stable) sort keeps each
    // group's own relative order otherwise.
    sortedPackages() {
        return [...this.packages].sort((a, b) => this.isPackageEnabled(b) - this.isPackageEnabled(a));
    },

    selectPackage(item) {
        if (!this.isPackageEnabled(item)) {
            return;
        }

        if (this.isGrouped()) {
            this.openGroupCheckout(item);

            return;
        }

        this.$dispatch('quick-checkout::open', {
            package: item,
            imageId: this.activeImage?.id,
            urlParams: this.urlParams,
            resetPhotosSelection: true,
        });
    },

    openGroupCheckout(item) {
        const imageIds = this.groupSelectedIds();

        if (imageIds.length === 0) {
            return;
        }

        this.$dispatch('quick-checkout::open', {
            package: item,
            imageId: imageIds[0],
            imageIds,
            urlParams: this.urlParams,
            resetPhotosSelection: true,
        });
    },

    calcContainerHeight(){
        this.windowHeight = window.innerHeight;

        document.querySelector('swiper-container')
            ?.classList.add('bg-gf-gray-950', 'flex-1', 'flex', 'flex-col', 'h-full', 'w-full');
    },

    appendSlides(position) {
        if (this.swiper?.virtual) {
            this.swiper.virtual.removeAllSlides();
        } else {
            this.swiper = null;
            this.swiperInitialize(position);
        }

        if (!this.swiper?.virtual) {
            return;
        }

        let slides = [];

        this.swiper.navigation.prevEl.innerHTML = `
            <svg part="button-previous-svg" width="40" height="40" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.19115 0.0381678C6.0998 0.000268724 5.99926 -0.00968732 5.90225 0.00955904C5.80524 0.0288054 5.71612 0.0763895 5.64615 0.146293L0.646155 5.14629C0.599667 5.19273 0.562787 5.24787 0.537625 5.30857C0.512463 5.36927 0.499512 5.43434 0.499512 5.50004C0.499512 5.56575 0.512463 5.63081 0.537625 5.69151C0.562787 5.75221 0.599667 5.80736 0.646155 5.85379L5.64615 10.8538C5.71608 10.9238 5.80521 10.9715 5.90225 10.9908C5.99929 11.0101 6.09989 11.0002 6.1913 10.9624C6.28271 10.9245 6.36083 10.8603 6.41577 10.778C6.4707 10.6957 6.49998 10.599 6.49991 10.5V0.500043C6.49988 0.401153 6.47054 0.304489 6.41558 0.222276C6.36063 0.140063 6.28253 0.0759933 6.19115 0.0381678Z" fill="#12447B"/>
            </svg>
        `;
        this.swiper.navigation.nextEl.innerHTML = `
            <svg part="button-next-svg" width="7" height="11" viewBox="0 0 7 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.35375 5.14616L1.35375 0.146155C1.28382 0.0761498 1.1947 0.0284661 1.09765 0.00914085C1.00061 -0.0101843 0.900016 -0.000282034 0.808605 0.0375942C0.717193 0.0754704 0.639075 0.139617 0.58414 0.221914C0.529205 0.304211 0.499922 0.400958 0.5 0.499905V10.4999C0.499922 10.5989 0.529205 10.6956 0.58414 10.7779C0.639075 10.8602 0.717193 10.9243 0.808605 10.9622C0.900016 11.0001 1.00061 11.01 1.09765 10.9907C1.1947 10.9713 1.28382 10.9237 1.35375 10.8537L6.35375 5.85366C6.40024 5.80722 6.43712 5.75207 6.46228 5.69138C6.48744 5.63068 6.50039 5.56561 6.50039 5.49991C6.50039 5.4342 6.48744 5.36913 6.46228 5.30844C6.43712 5.24774 6.40024 5.19259 6.35375 5.14616Z" fill="#12447B"/>
            </svg>
        `;

        Object.values(this.photos).forEach((photo) => {
            const column = this.watermark ? 'watermark_preview' : this.column;
            const preview = _.get(photo, column);

            slides.push(`
                <swiper-slide
                    id='slider-preview-box-${photo.id}'
                    class='h-full w-full flex items-end bg-contain bg-no-repeat bg-center'
                    style="background-image: url('${preview}')"
                >
                    <div class="bg-white px-2 py-1 text-xs" x-text="'${photo?.name || 'loading...'}'"></div>
                </swiper-slide>
            `);
        });

        this.swiper.virtual.appendSlide(slides);
        this.swiper.slideTo(position, 1);

        this.setActiveImage();
    },

    swiperInitialize() {
        if(this.swiper) {
            return;
        }

        const swiperEl = document.querySelector('swiper-container');

        if (!swiperEl) {
            return;
        }

        if (!swiperEl.swiper) {
            swiperEl.initialize();
        }

        this.swiper = swiperEl.swiper;

        if (!this.swiper) {
            return;
        }

        this.swiper.on('activeIndexChange', async (event) => {
            const swiper = await event;
            const currentIndex = swiper.activeIndex;

            if (this.previousActiveIndex === currentIndex) {
                return;
            }

            if (this.skipGroupMembers(currentIndex)) {
                return;
            }

            const isGoingForward = this.previousActiveIndex < currentIndex;
            const forwardOffset   = !this.lastForwardOffset ? this.lastLoadedOffset : this.lastForwardOffset;
            const backwardOffset = !this.lastBackwardOffset ? this.lastLoadedOffset : this.lastBackwardOffset;

            if (isGoingForward && currentIndex + (this.perPage / 2) < forwardOffset) {
                this.setActiveImage();

                this.previousActiveIndex = currentIndex;

                return;
            }

            if (!isGoingForward && currentIndex - (this.perPage / 2) > backwardOffset) {
                this.setActiveImage();

                this.previousActiveIndex = currentIndex;

                return;
            }

            if (this.isLoadingImages) {
                return;
            }

            const currentOffset = isGoingForward
                ? forwardOffset + (this.firstLoad ? 0 : this.perPage)
                : Math.max(0, backwardOffset - this.perPage);

            const currentOffsetKey = isGoingForward ? `forward-${currentOffset}` : `backward-${currentOffset}`;

            if (this.requestedOffsets.includes(currentOffsetKey) && !this.firstLoad) {
                this.setActiveImage();

                this.previousActiveIndex = currentIndex;

                return;
            }

            this.firstLoad = false;
            this.isLoadingImages = true;
            this.previousActiveIndex = currentIndex;

            try {
                const newImages = await this.loadMoreImages(currentOffset, currentIndex);
                this.photos = { ...this.photos, ...newImages };

                if (!this.requestedOffsets.includes(currentOffsetKey)) {
                    this.requestedOffsets.push(currentOffsetKey);
                }

                this.lastLoadedOffset = currentOffset;

                if (isGoingForward) {
                    this.lastForwardOffset = currentOffset;
                } else {
                    this.lastBackwardOffset = currentOffset;
                }

                this.appendSlides(currentIndex);
            } finally {
                this.isLoadingImages = false;
            }
        });
    },

    setActiveImage() {
        this.activeIndex = this.swiper?.activeIndex ?? this.activeIndex;
        this.activeImage = this.photos[this.activeIndex];

        this.syncEntry();
    },

    async loadMoreImages(offset = 0, currentIndex) {
        const params = {};

        const image = this.photos[currentIndex];
        if (!image) return {};

        if (this.route === 'event') {
            params.eventId = image?.event_id ?? this.currentEventId;
        } else if (this.route === 'customer') {
            params.mainEventId = image.main_event_id;
        }

        params.offset = offset;
        params.limit = this.perPage;

        const response = await fetch(`/${this.route}/images?` + new URLSearchParams(params));

        if (!response.ok) {
            throw new Error(`Failed to load images: ${response.status}`);
        }

        const { data } = await response.json();
        const merged = {};

        data.forEach((photo, i) => {
            merged[offset + i] = photo;
        });

        return merged;
    },

    updateRequestData({ offsets, limit }) {
        this.perPage = limit;

        const mergedOffsets = new Set([...this.requestedOffsets, ...offsets]);
        this.requestedOffsets = [...mergedOffsets];
        this.lastLoadedOffset = this.requestedOffsets[this.requestedOffsets.length - 1] || limit;
    },

    save() {
        const favorite = this.activeImage?.favorite ?? false;
        this.activeImage.old_favorite = favorite;

        this.$store.customerImages.toggleImage(this.activeImage, this.checkImages);
        this.update(this.activeImage, 'saved');
    },

    favorite() {
        this.$store.customerImages.toggleFavoriteImage(this.activeImage, this.mainEventId);
        this.update(this.activeImage, 'favorite');
    },

    select() {
        if(!this.canSelectPhoto() && !this.selected()) {
            return;
        }

        if(_.get(this.imagesSelected, this.activeImage?.id, false)) {
            delete this.imagesSelected[this.activeImage.id];
        } else {
            this.imagesSelected[this.activeImage.id] = this.activeImage;
        }

        this.$dispatch(this.event + '::selected', this.imagesSelected);
    },

    canSelectPhoto() {
        return this.canSelect && (Object.keys(this.imagesSelected).length < this.quantity);
    },

    saved() {
        return this.activeImage?.saved ?? false;
    },

    checkPaid(){
        return this.activeImage?.paid
    },

    notPrinted(){
        return this.activeImage?.print_only === 0
    },

    readyToDownload(){
        return !_.isEmpty(this.activeImage?.sent_at) && this.notPrinted()
    },

    favorited() {
        return this.activeImage?.favorite ?? false;
    },

    selected() {
        return _.get(this.imagesSelected, this.activeImage?.id, false);
    },

    download() {
        this.$dispatch('track-download', { photo: { main_event_id: this.activeImage?.main_event_id } });

        if (this.$isMobileSafari()) {
            this.$dispatch('view-photo-modal::open', this.activeImage);

            this.downloading = false;

            this.$dispatch(this.event, { image: this.activeImage });

            if (this.isSaved(image)) return;

            this.$store.customerImages.toggleImage(image);

            return;
        }

        this.downloading = true;

        let name = this.activeImage?.name.replace(/\.jpg$/, '') ?? '';
        let url = this.activeImage.high_res ?? document.querySelector(`img.preview[data-index="${this.swiper.activeIndex}"]`).currentSrc;

        this.$downloadImage(url, name ?? 'Glossy-Finish-image', () => {
            if (this.$store.customerImages.isSaved(this.activeImage)) return;

            this.$store.customerImages.toggleImage(this.activeImage);
        });

        setTimeout(() => {
            this.downloading = false;
        }, 500);
    },

    close() {
        this.opened = false;

        this.dispatchScrollToGrid();
    },

    dispatchScrollToGrid() {
        const index = this.swiper?.activeIndex;

        if (index == null || index === this.openedAtIndex) {
            return;
        }

        const target = this.scrollTarget
            ?? this.event?.split('::').slice(0, -1).join('::');

        if (!target) {
            return;
        }

        this.$dispatch(`${target}::scroll-to`, { index });
    },

    update(image=null, action=null) {
        if(this.event === null) {
            return;
        }

        this.$dispatch(this.event, { image, action });
    },

    checkImage(element, photoId) {
        element.onload = () => document.querySelectorAll(`[data-loading=loading-${photoId}]`)?.forEach(
            element => element.remove()
        );

        if(element.complete) {
            element.onload()
        }
    },

    flag(item) {
        return !item.is_digital && !item.shipping_price ? 'Free Delivery' : null;
    },
})
